import { useMsal } from '@azure/msal-react';
import { ICommandBarItemProps, IOverflowSetItemProps, Text, useTheme } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { AddIcon } from '@fluentui/react-icons-mdl2';
import { Stack } from '@fluentui/react/lib/Stack';
import { RouterParams } from 'App';
import { RootState } from 'app/store';
import CustomPanel from 'components/controls/CustomPanel';
import SimpleCard from 'components/controls/SimpleCard';
import {
    AppLinkTo,
    EViewType,
    compareUnderWritingReviewToWithoutDispatch,
    emitCustomEvent
} from 'global/Application';
import { useApplication } from 'global/Application/Context';
import { MsalTokenSource } from 'global/DataSources/MsalTokenSource';
import { removePlanFeesAndPremiumsReference, updatePlanFeesAndPremiums } from 'global/Reducer/dataSlice';
import { BuildVisibilityScope } from 'global/Visibility/GlobalVisibility';
import { IComponentContextMenu } from 'model/Application';
import { bcbs_groupconfigurationsnapshot } from 'model/AttributeOptionSets.type';
import {
    IBenefit as FAM_IBenefit,
    IPlan as FAM_IPlan,
    IRef as FAM_IRef
} from "model/FlexibleAttributeModel";
import { useEffect, useMemo, useState } from "react";
import { ConnectedProps, connect, useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { AppDataSource } from '../../global/DataSources/AppDataSource';
import BenefitWizard from './BenefitWizard';
import GroupConfigurationSnapshotContext from './GroupConfigurationSnapshotContext';
import BenefitPanel from './Controls/BenefitPanel';
import FeesAndPremiumCard from './FeesAndPremiumCard';
import FeesAndPremiumWizard from './FeesAndPremiumWizard';
import FlexibleFieldView, { EScopeType } from './FlexibleFieldView';
import GroupStructureGroupNumberView from './GroupStructureGroupNumberView';
import MonthlyRecurringFees from './MonthlyRecurringFees';
import PlanSummary from './Plan/PlanSummary';
import PlanProperties from './PlanProperties';
import SBCWizard from './SBCWizard/SBCWizard';


interface IPlan extends TComponentConnectedProps, IComponentContextMenu {
    plan?: FAM_IPlan;
    benefits?: FAM_IBenefit[];
}

const Plan = (props: IPlan) => {

    const { planId } = useParams<RouterParams>();
    const state = useSelector((state: RootState) => state);
    const disabled = props.state.status.viewType !== EViewType.Modify;

    const history = useHistory();
    const application = useApplication();

    const theme = useTheme();
    const dispatch = useDispatch();
    const [benefitWizardIsOpen, { setTrue: benefitWizardOpen, setFalse: benefitWizardClose }] = useBoolean(false);
    const [planPropertiesIsOpen, setPlanPropertiesIsOpen] = useState(false)

    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    //Dev Validation, if application status is not what we expect
    if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development')
        if (!planId && !props.plan)
            throw new Error("Plan ID missing");

    //TODO use memo, needs a manager context
    // const plan = useMemo(() => {
    //     return props.state?.data?.plans?.find(q => q.id === planId);
    // }, []);

    const plan = props.plan ? props.plan : props.state?.data.json?.plans?.find(q => q.id === planId)!;
    const benefits: FAM_IBenefit[] = props.plan ? props.benefits! : props.state?.data.json?.benefits!;

    const feesAndPremium = props?.state?.data.json?.feesAndPremiums?.find(b => b.id === plan?.feesAndPremiumsRef?.id)

    const [feesAndPremiumWizardIsOpen, { setTrue: feesAndPremiumWizardOpen, setFalse: feesAndPremiumWizardClose }] = useBoolean(false);
    const [sbcWizardIsOpen, setSbcWizardIsOpen] = useState(false);

    const workspace = props.state.status.workspace;

    const categoryPlan = props.state.data.config?.cache?.map_bcbs_config_category_named['Plan']

    useEffect(() => {
        document.title = `Plan`;

        if (plan?.name)
            document.title = `Plan | ${plan?.name}`;

    }, []);




    const contextMenu = useMemo(() => {

        const menu: ICommandBarItemProps[] = [];

        // Plan Properties
        if (!disabled)
            menu.push(
                {
                    key: 'plan',
                    text: 'Properties',
                    iconProps: { iconName: 'Settings' },
                    onClick: () => setPlanPropertiesIsOpen(true)
                })

        // Generate SBC Option
        menu.push({
            key: 'sbc',
            text: 'SBC',
            title: 'Generate SBC',
            iconProps: { iconName: 'Generate' },
            onClick: (ev, item) => {
                setSbcWizardIsOpen(true);
            }
        });

        // Generate BNC Option
        menu.push({
            key: 'bnc',
            text: 'B&C',
            title: 'Generate B&C',
            iconProps: { iconName: 'Generate' },
            onClick: (ev, item) => {
                (async () => {
                    try {
                        const BNCGenerateActionImport = await import('global/Application/BNCGenerateAction');
                        const { BNCGenerateAction } = BNCGenerateActionImport;
                        dispatch(BNCGenerateAction(dataSource, plan));
                    } catch (error) {
                        emitCustomEvent({
                            eventType: 'globalException',
                            details: error
                        });
                    }
                })();
            }
        });

        menu.push({
            key: 'menu.plan.pharmacy.ore',
            text: 'Pharmacy/ORE',
            iconProps: { iconName: 'Pill' },
            onClick: () => {
                const foundSnapshot = compareUnderWritingReviewToWithoutDispatch(props.state, props.state.data.snapshot);
                // TODO move this to AppNavigateToExternal
                (window as Window).location = (`/pharmacy/${state.data.snapshot._bcbs_groupid_value}/${state.data.snapshot.bcbs_groupconfigurationsnapshotid}/${planId}/${foundSnapshot?.bcbs_groupconfigurationsnapshotid ?? ""}`);
            }
        });

        menu.push({
            key: 'menu.plan.pharmacy.pre',
            text: 'Pharmacy/PRE',
            iconProps: { iconName: 'Pill' },
            onClick: () => {
                history.push(AppLinkTo(props.state, { type: 'floridaPrimeRxPlan', id: planId! }))
            }
        });

        if (
            props.state.data.snapshot.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Pending Underwriting(808900009)'].value ||
            props.state.data.snapshot.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Gold Standard(808900006)'].value ||
            props.state.data.snapshot.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Underwriting Review Complete(2)'].value ||
            props.state.data.snapshot.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Retired(808900001)'].value ||
            props.state.data.snapshot.statuscode === bcbs_groupconfigurationsnapshot.statuscode['MNIL Underwriting Complete(808900004)'].value
        )
            menu.push({
                key: 'enmChecklist',
                text: 'E&M Checklist',
                iconProps: { iconName: 'CheckList' },
                onClick: () => {
                    history.push(AppLinkTo(props.state, { id: planId!, type: 'checkListWithPlan' }))
                }
            });


        menu.push({
            key: 'loadGroupHeaders',
            text: 'Load Group Headers',
            iconProps: { iconName: 'LargeGrid' },
            onClick: () => {
                history.push(`/groupHeaders/${state.data.snapshot._bcbs_groupid_value}/${state.data.snapshot.bcbs_groupconfigurationsnapshotid}/${planId}`);
            }
        });


        return { items: menu, key: 'plan' };
    }, [plan, disabled]);


    const controlVisibility = BuildVisibilityScope(
        null,
        props.state.data.json,
        null,
        plan
    );

    const onRenderSummary = () => {
        return <CustomPanel title={"Summary"}
            canCollapse={true}
            defaultCollapsed={true}
            storageId={"plan.summary"}
        >
            <PlanSummary
                plan={plan}
            />
        </CustomPanel>
    }

    const onRenderBenefits = () => {
        return <BenefitPanel
            onAdd={benefitWizardOpen}
            benefitsPlan={plan}
        />;
    }

    const onRenderAttributes = () => {
        return <CustomPanel title={"Attributes"}
            canCollapse={true}
            defaultCollapsed={false}
            contentStyle={{ overflow: 'visible' }}
            storageId={"plan.attributes"}
        >
            <FlexibleFieldView
                disabled={disabled}
                fields={plan?.fields}
                tiers={[]}
                category={categoryPlan}
                scope={plan}
                visibility={controlVisibility}
                scopeType={EScopeType.plan}
            />
        </CustomPanel>;
    }

    const onRenderGroupNumbers = () => {
        return <CustomPanel title={"Group Numbers"}
            canCollapse={true}
            defaultCollapsed={true}
            storageId={"plan.groupNumbers"}
            contentStyle={{ overflow: 'visible' }}
        >
            <div style={{ padding: '8px' }}></div>

            <GroupStructureGroupNumberView
                currentPlan={plan}
                disabled={disabled}
                onPick={(index) => {

                }}
            />
        </CustomPanel>;
    }

    const onRenderFeesAndPremium = () => {
        {/* <Fees {...props} /> */ }

        const planFees: FAM_IRef[] = [];

        if (plan?.feesAndPremiumsRef)
            planFees.push(plan.feesAndPremiumsRef)

        return <CustomPanel title={"Fees And Premiums"}
            storageId={"plan.feesAndPremiums"}
            canCollapse={true}
            defaultCollapsed={true}
            contentStyle={{ overflow: 'visible' }}
        >
            <Stack
                horizontal
                wrap
                verticalFill
                tokens={{ childrenGap: 15 }}
                horizontalAlign="start"
                style={{ padding: '15px' }}
            >
                {planFees.map(planFee => {

                    const menu: IOverflowSetItemProps[] = [];
                    if (props.state.status.viewType === EViewType.Modify && application.ChangeTypePermissions.plan.link_feesandpremium)
                        menu.push({
                            key: 'remove',
                            name: 'Remove',
                            icon: 'Edit',
                            onClick: () => {
                                dispatch(removePlanFeesAndPremiumsReference({ planRef: plan }));
                            }
                        });


                    return (
                        <FeesAndPremiumCard
                            key={planFee.id}
                            description={planFee.name}
                            overflowItems={menu}
                            state={props.state}
                            feesAndPremium={feesAndPremium!}
                            onClick={() => history.push(AppLinkTo(props.state, {
                                id: planFee.id,
                                type: 'feesAndPremium',
                                // parent: {
                                //     id: plan.id,
                                //     type: 'plan'
                                // }
                            }))}
                        />
                    )
                })}

                {!disabled && (!plan?.feesAndPremiumsRef?.id) && application.ChangeTypePermissions.plan.link_feesandpremium &&
                    <SimpleCard
                        key={"defaultFeesAndPremiums"}
                        onClick={() => feesAndPremiumWizardOpen()}
                        onRenderFront={() => {
                            return (
                                <>
                                    <p><Text>Add</Text></p>
                                    <AddIcon style={{ fontSize: 'x-large', color: theme.palette.themePrimary }} />
                                </>
                            );
                        }}
                    />
                }


            </Stack>
        </CustomPanel >;
    }

    const onRenderSBCWizard = () => {
        return <SBCWizard
            plan={plan}
            isOpen={sbcWizardIsOpen}
            onDismiss={() => {
                setSbcWizardIsOpen(false);
            }}
        />;
    }

    const onRenderMonthlyRecurringFees = () => {
        if (!feesAndPremium)
            return <></>;

        return <MonthlyRecurringFees feesAndPremium={feesAndPremium} variant="rate" />;
    }

    return <GroupConfigurationSnapshotContext contextMenu={contextMenu}>
        {plan &&
            <>
                {props.state.status.viewType === EViewType.Modify &&
                    <>
                        <BenefitWizard
                            isOpen={benefitWizardIsOpen}
                            onCancel={benefitWizardClose}
                            onDismiss={benefitWizardClose}
                            currentPlan={plan}
                        />
                        <PlanProperties
                            isOpen={planPropertiesIsOpen}
                            onDismiss={() => setPlanPropertiesIsOpen(false)}
                            value={{ plan: plan }}
                        />
                        <FeesAndPremiumWizard
                            isOpen={feesAndPremiumWizardIsOpen}
                            onCancel={feesAndPremiumWizardClose}
                            onDismiss={feesAndPremiumWizardClose}
                            variant={'entry'}
                            currentPlan={plan}
                            onConfirm={(e) => {
                                if (plan && e?.value?.feesAndPremiums) {
                                    const feeAndPremium = e.value.feesAndPremiums[0];
                                    dispatch(updatePlanFeesAndPremiums({
                                        planRef: plan,
                                        feesAndPremiumRef: feeAndPremium
                                    }))
                                }

                                feesAndPremiumWizardClose();
                            }}
                        />
                    </>
                }

                {onRenderSummary()}
                {onRenderBenefits()}
                {onRenderAttributes()}
                {onRenderGroupNumbers()}
                {onRenderFeesAndPremium()}
                {onRenderSBCWizard()}
                {onRenderMonthlyRecurringFees()}


            </>
        }
    </GroupConfigurationSnapshotContext>
}


function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connect(mapStateToProps)(Plan)